import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useGetList } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';

export const CalendarSettings = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    calendarStartTime: '08:00:00',
    calendarEndTime: '17:00:00',
    slotDuration: '00:15:00',
    defaultViewMode: 'timeGridWeek',
  });

  // Load current tenant settings
  const { data: tenantSettings, refetch } = useGetList('crm/tenant-settings', {
    pagination: { page: 1, perPage: 1 },
  });

  useEffect(() => {
    if (tenantSettings && tenantSettings.length > 0) {
      const currentSettings = tenantSettings[0];
      setSettings({
        calendarStartTime: currentSettings.calendarStartTime || '08:00:00',
        calendarEndTime: currentSettings.calendarEndTime || '17:00:00',
        slotDuration: currentSettings.slotDuration || '00:15:00',
        defaultViewMode: currentSettings.defaultViewMode || 'timeGridWeek',
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [tenantSettings]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (tenantSettings && tenantSettings.length > 0) {
        // Update existing settings
        await dataProvider.update('crm/tenant-settings', {
          id: tenantSettings[0].id,
          data: settings,
          previousData: tenantSettings[0],
        });
      } else {
        // Create new settings
        await dataProvider.create('crm/tenant-settings', {
          data: settings,
        });
      }
      notify('Calendar settings saved successfully', { type: 'success' });
      refetch();
    } catch (error) {
      notify('Failed to save calendar settings', { type: 'error' });
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Calendar Settings"
        subtitle="Configure calendar display and business hours"
        icon={<CalendarIcon />}
      />
      
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/configuration')}
            sx={{ mb: 2 }}
          >
            Back to Configuration
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Business Hours
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the default business hours for calendar views. These times will be used as the visible hours in day and week views.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Time"
                type="time"
                value={settings.calendarStartTime.slice(0, 5)}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  calendarStartTime: `${e.target.value}:00`
                }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900 }} // 15 minute steps
                helperText="Calendar day view will start at this time"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Time"
                type="time"
                value={settings.calendarEndTime.slice(0, 5)}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  calendarEndTime: `${e.target.value}:00`
                }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900 }} // 15 minute steps
                helperText="Calendar day view will end at this time"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Time Slot Duration"
                select
                value={settings.slotDuration}
                onChange={handleChange('slotDuration')}
                fullWidth
                SelectProps={{ native: true }}
                helperText="Duration of each time slot in calendar views"
              >
                <option value="00:15:00">15 minutes</option>
                <option value="00:30:00">30 minutes</option>
                <option value="01:00:00">1 hour</option>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Default View"
                select
                value={settings.defaultViewMode}
                onChange={handleChange('defaultViewMode')}
                fullWidth
                SelectProps={{ native: true }}
                helperText="Default calendar view when opening the calendar"
              >
                <option value="dayGridMonth">Month</option>
                <option value="timeGridWeek">Week</option>
                <option value="timeGridDay">Day</option>
                <option value="listWeek">List</option>
              </TextField>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            These settings will apply to all calendar views including the main calendar and task calendar.
          </Alert>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};